import React from "react";
import axios from "axios";
import { Building2, BookOpen, Users, Calendar, CalendarIcon, CheckCircle2, Trash2 } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { API, priorityColors, statusColors, statusIcons, getSiteName, getStudyName } from "../utils/helpers";

export const TaskCard = ({ task, onClick, sites, studies, tasks, setTasks, updateTaskStatus, confirmDeleteTask }) => (
  <Card data-testid={`task-card-${task.id}`} className="mb-3 hover:shadow-md transition-shadow cursor-pointer" onClick={() => onClick(task)}>
    <CardHeader className="pb-2">
      <div className="flex items-start justify-between">
        <CardTitle className="text-sm font-medium">{task.title}</CardTitle>
        <Badge className={`text-xs ${priorityColors[task.priority]}`}>
          {task.priority}
        </Badge>
      </div>
      <CardDescription className="text-xs">{task.description}</CardDescription>
    </CardHeader>
    <CardContent className="pt-0">
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-xs text-gray-600">
          <Building2 className="h-3 w-3" />
          <span>{getSiteName(sites, task.site_id)}</span>
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-600">
          <BookOpen className="h-3 w-3" />
          <span>{getStudyName(studies, task.study_id)}</span>
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-600">
          <Users className="h-3 w-3" />
          <span>{task.assignee}</span>
        </div>
        {task.visit && (
          <div className="flex items-center gap-2 text-xs text-gray-600">
            <CalendarIcon className="h-3 w-3" />
            <span className="font-medium">Visit:</span>
            <span>{task.visit}</span>
          </div>
        )}
        
        {/* Three Date System Display */}
        <div className="space-y-1">
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <Calendar className="h-3 w-3" />
            <span className="font-medium">Planned:</span>
            <span>{task.planned_date ? new Date(task.planned_date).toLocaleDateString() : 'Not set'}</span>
          </div>
          <div className="flex items-center gap-2 text-xs text-blue-600">
            <Calendar className="h-3 w-3" />
            <span className="font-medium">Current:</span>
            <span>{task.sliding_date ? new Date(task.sliding_date).toLocaleDateString() : 'Not set'}</span>
          </div>
          {task.actual_completion_date && (
            <div className="flex items-center gap-2 text-xs text-green-600">
              <CheckCircle2 className="h-3 w-3" />
              <span className="font-medium">Completed:</span>
              <span>{new Date(task.actual_completion_date).toLocaleDateString()}</span>
            </div>
          )}
        </div>
        
        {/* Subtasks Checklist */}
        {task.subtasks && task.subtasks.length > 0 && (
          <div className="mt-3 pt-2 border-t border-gray-200">
            <div className="text-xs font-semibold text-gray-700 mb-2">
              QC Checklist ({task.subtasks.filter(st => st.completed).length}/{task.subtasks.length} completed)
            </div>
            <div className="space-y-1 max-h-40 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              {task.subtasks.map((subtask, index) => (
                <div key={`${task.id}-subtask-${index}-${subtask.text?.slice(0,20)}`} className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    checked={subtask.completed}
                    onChange={async (e) => {
                      e.stopPropagation();
                      const updatedSubtasks = task.subtasks.map((st, i) => 
                        i === index ? { ...st, completed: e.target.checked } : st
                      );
                      try {
                        await axios.put(`${API}/tasks/${task.id}`, { subtasks: updatedSubtasks });
                        setTasks(tasks.map(t => 
                          t.id === task.id ? { ...t, subtasks: updatedSubtasks } : t
                        ));
                        toast.success("Checklist item updated");
                      } catch (error) {
                        
                        toast.error("Failed to update checklist");
                      }
                    }}
                    className="mt-1 h-3 w-3 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <label className={`text-xs ${subtask.completed ? 'line-through text-gray-400' : 'text-gray-600'}`}>
                    {subtask.text}
                  </label>
                </div>
              ))}
            </div>
          </div>
        )}
        
        <div className="flex items-center justify-between">
          <Badge className={`text-xs ${statusColors[task.status]}`}>
            <span className="flex items-center gap-1">
              {statusIcons[task.status]}
              {task.status.replace('_', ' ')}
            </span>
          </Badge>
          {task.template_type && (
            <Badge variant="outline" className="text-xs">
              {task.template_type.replace('_', ' ')}
            </Badge>
          )}
        </div>
        <div className="flex gap-1 mt-2" onClick={(e) => e.stopPropagation()}>
          {task.status !== 'completed' && (
            <Button 
              size="sm" 
              variant="outline" 
              className="text-xs h-6 px-2"
              onClick={() => {
                const nextStatusMap = { todo: 'in_progress', in_progress: 'delayed' };
                const nextStatus = nextStatusMap[task.status] || 'completed';
                updateTaskStatus(task.id, nextStatus);
              }}
            >
              {(() => {
                const labelMap = { todo: 'Start', in_progress: 'Delay' };
                return labelMap[task.status] || 'Complete';
              })()}
            </Button>
          )}
          {task.status !== 'completed' && task.status !== 'delayed' && (
            <Button 
              size="sm" 
              variant="outline" 
              className="text-xs h-6 px-2 text-orange-600 hover:text-orange-800 hover:bg-orange-50"
              onClick={() => updateTaskStatus(task.id, 'delayed')}
              title="Mark as delayed"
            >
              Delayed
            </Button>
          )}
          <Button 
            size="sm" 
            variant="outline" 
            className="text-xs h-6 px-2 text-red-600 hover:text-red-800 hover:bg-red-50"
            onClick={() => confirmDeleteTask(task.id, task.title)}
            title="Delete task"
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      </div>
    </CardContent>
  </Card>
);
